<!--
  ApprovalDetailPanel.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Right pane of the Approval Workspace. Composes the asset header,
  lifecycle stepper, stale callout, status-aware action bar, approval
  thread, version history, and where-used panel into a single
  scrollable detail view for the currently-selected ApprovalRecord.

  This component is a "presentation shell + action forwarder." All
  state lives in the Pinia store; the panel:

    1. Reads the detail off ``store.selectedRecord`` + its loading flag.
    2. Wires each action-bar emission to the matching store action.
    3. Manages a small amount of *local* state for the ReasonPicker
       dialog (which reason / which notes the user is composing).

  Why the picker state is local: the picker opens, the user picks a
  reason, and submits -- only on submit does the store get called.
  Keeping the in-flight dialog state out of the store keeps the store
  contract focused on persisted record state.

  Props:
    - store: object -- the Pinia useApprovalWorkspaceStore() instance,
      injected by the page so this component can be mount-tested
      against a fake store object.
-->
<template>
    <section class="aw-detail" data-testid="approval-detail-panel">
        <div
            v-if="!record && !loading"
            class="aw-detail__placeholder"
            data-testid="approval-detail-placeholder"
        >
            <h3 class="aw-detail__placeholder-title">
                Select an approval to review.
            </h3>
            <p class="aw-detail__placeholder-body">
                Pick an item from the queue on the left to see its
                lifecycle stepper, approval thread, and version history.
            </p>
        </div>
        <div
            v-if="loading && !record"
            class="aw-detail__loading"
            data-testid="approval-detail-loading"
        >
            Loading approval details...
        </div>
        <div v-if="record" class="aw-detail__content">
            <StaleCallout
                :stale-state="record.stale_state"
                :pending="store.actionPending"
                @remind="onRemind"
                @escalate="onEscalate"
                @bypass-open="openReasonPicker"
            />

            <header class="aw-detail__header">
                <span class="aw-detail__thumb" aria-hidden="true">
                    {{ thumbLetter }}
                </span>
                <div class="aw-detail__head-body">
                    <h2
                        class="aw-detail__title"
                        data-testid="approval-detail-title"
                    >
                        {{ displayName }}
                    </h2>
                    <div class="aw-detail__head-meta">
                        <span class="aw-detail__chip aw-detail__chip--status"
                            >{{ statusLabel }}</span
                        >
                        <span
                            v-if="versionNumber != null"
                            class="aw-detail__chip"
                            >v{{ versionNumber }}</span
                        >
                        <span
                            class="aw-detail__chip"
                            :class="{
                                'aw-detail__chip--override':
                                    isDistributorOverride,
                            }"
                            data-testid="approval-detail-approver"
                            >{{ approverLine }}</span
                        >
                    </div>
                </div>
            </header>

            <LifecycleStepper :status="effectiveStatus" />

            <!-- PR-8: Wilcom Trueview preview for EMB artworks -->
            <div
                v-if="record.artwork_preview"
                class="aw-detail__trueview"
                data-testid="approval-trueview-section"
            >
                <DecorationArtThumb
                    v-if="record.artwork_preview.state === 'ready'"
                    :src="record.artwork_preview.preview_url"
                    :ink-hex="record.artwork_preview.ink_hex || null"
                    :ink-mixed="record.artwork_preview.ink_mixed === true"
                    class="aw-detail__trueview-img"
                    alt="Trueview embroidery preview"
                    data-testid="approval-trueview-img"
                />
                <div
                    v-else-if="
                        record.artwork_preview.state === 'pending' ||
                        record.artwork_preview.state === 'rendering'
                    "
                    class="aw-detail__trueview-pending"
                    data-testid="approval-trueview-pending"
                >
                    <span class="aw-detail__trueview-spinner" aria-hidden="true" />
                    <span>Generating preview&hellip;</span>
                </div>
                <div
                    v-else-if="record.artwork_preview.state === 'failed_retryable'"
                    class="aw-detail__trueview-pending"
                    data-testid="approval-trueview-pending"
                >
                    <span class="aw-detail__trueview-spinner" aria-hidden="true" />
                    <span>Generating preview&hellip;</span>
                </div>
                <div
                    v-else-if="record.artwork_preview.state === 'failed_permanent'"
                    class="aw-detail__trueview-failed"
                    data-testid="approval-trueview-failed"
                >
                    Preview unavailable
                </div>
            </div>

            <StatusAwareActionBar
                :status="effectiveStatus"
                :pending="store.actionPending"
                @send-to-digitizer="onSendToDigitizer"
                @send-reminder="onRemind"
                @resend-request="onResend"
                @escalate="onEscalate"
                @bypass-open="openReasonPicker"
                @resend-digitizer="onResendToDigitizer"
                @re-request="onResend"
            />

            <p
                v-if="store.actionError"
                class="aw-detail__action-error"
                data-testid="approval-detail-action-error"
            >
                {{ store.actionError }}
            </p>

            <p
                v-if="store.actionNotice"
                class="aw-detail__action-notice"
                data-testid="approval-detail-action-notice"
            >
                {{ store.actionNotice }}
            </p>

            <ApprovalThread
                :messages="threadMessages"
                :can-post="canPostMessages"
                :pending="store.actionPending"
                @post="onPostMessage"
            />

            <ReworkPanel
                :prefill="store.reworkPrefill"
                :can-submit="canSubmitRework"
                :pending="store.actionPending"
                :error="store.actionError"
                @submit="onSubmitRework"
            />

            <VersionHistory
                :versions="versions"
                :artwork-id="record.artwork_id"
                :can-restore="canRestoreVersions"
                :pending="store.actionPending"
                @restore="onRestoreVersion"
            />

            <WhereUsedPanel
                :usage="currentUsage"
                :sellable="record.status === 'approved'"
                :loading="usageLoading"
            />
        </div>

        <div
            v-if="reasonPickerOpen"
            class="aw-detail__modal-backdrop"
            data-testid="approval-detail-bypass-modal"
            @click.self="cancelReasonPicker"
        >
            <div class="aw-detail__modal">
                <h3 class="aw-detail__modal-title">Bypass approval</h3>
                <p class="aw-detail__modal-intro">
                    Mark this artwork approved without a client click.
                    A reason is required and recorded on the audit trail.
                </p>
                <ReasonPicker
                    v-model="reasonId"
                    :reasons="store.bypassReasons"
                    :notes="reasonNotes"
                    :required="true"
                    confirm-label="Bypass and approve"
                    cancel-label="Cancel"
                    @update:notes="onReasonNotes"
                    @confirm="confirmBypass"
                    @cancel="cancelReasonPicker"
                />
            </div>
        </div>
    </section>
</template>

<script>
import { formatArtworkName } from '@/utils/formatArtworkName'
import LifecycleStepper from './LifecycleStepper.vue'
import StatusAwareActionBar from './StatusAwareActionBar.vue'
import ApprovalThread from './ApprovalThread.vue'
import ReworkPanel from './ReworkPanel.vue'
import VersionHistory from '@/components/decoration/VersionHistoryPanel.vue'
import WhereUsedPanel from '@/components/decoration/WhereUsedPanel.vue'
import DecorationArtThumb from '@/components/decoration/DecorationArtThumb.vue'
import StaleCallout from './StaleCallout.vue'
import ReasonPicker from '@/components/ReasonPicker.vue'
import { STATUS_LABELS } from './filters'

export default {
    name: 'ApprovalDetailPanel',
    components: {
        DecorationArtThumb,
        LifecycleStepper,
        StatusAwareActionBar,
        ApprovalThread,
        ReworkPanel,
        VersionHistory,
        WhereUsedPanel,
        StaleCallout,
        ReasonPicker,
    },
    props: {
        store: {
            type: Object,
            required: true,
        },
        canWrite: {
            // Builder + Owner both hold ``distributor.decoration_library.
            // write``; pass it in so the page can drop the action surface
            // for read-only callers (e.g. a future read-only SuperAdmin
            // view).
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            reasonPickerOpen: false,
            reasonId: null,
            reasonNotes: '',
        }
    },
    computed: {
        record() {
            return this.store.selectedRecord
        },
        loading() {
            return this.store.detailLoading
        },
        threadMessages() {
            return this.record?.thread_messages || []
        },
        versions() {
            // The artwork's version list arrives via the detail record's
            // ``artwork.versions`` if the F1.e payload inlined them; if
            // not, the parent page hydrates the artwork separately.
            return (
                this.record?.versions ||
                this.record?.artwork?.versions ||
                []
            )
        },
        currentUsage() {
            const aid = this.record?.artwork_id
            if (aid == null) {
                return { campaign_products: [], stores: [] }
            }
            return (
                this.store.usageByArtwork[aid] || {
                    campaign_products: [],
                    stores: [],
                }
            )
        },
        usageLoading() {
            const aid = this.record?.artwork_id
            if (aid == null) return false
            return !!this.store.usageLoadingByArtwork[aid]
        },
        canPostMessages() {
            return this.canWrite && !!this.record
        },
        canSubmitRework() {
            // Two conditions, both necessary. ``canWrite`` is the caller's
            // capability; ``store.reworkCanSubmit`` is whether a reworkable
            // digitizer job exists behind this record at all (an artwork
            // that was never sent out, or whose version was minted by hand,
            // has nothing to revise). Neither implies the other, and the
            // API re-checks both -- this only decides whether to render the
            // control.
            return this.canWrite && this.store.reworkCanSubmit
        },
        canRestoreVersions() {
            // Restore is only meaningful when there's a digitizer round
            // to revisit -- read-only statuses (approved) can still
            // restore for a re-run loop, so we keep the gate open while
            // the user has write capability.
            return this.canWrite
        },
        effectiveStatus() {
            const s = this.record?.status
            if (s) return s
            return 'awaiting_approval'
        },
        statusLabel() {
            return STATUS_LABELS[this.effectiveStatus] || this.effectiveStatus
        },
        versionNumber() {
            const r = this.record
            if (!r) return null
            return (
                r.artwork_version?.version_number ??
                r.artwork?.version ??
                null
            )
        },
        displayName() {
            return (
                formatArtworkName(
                    this.record?.artwork?.filename ||
                        this.record?.artwork?.original_filename,
                ) || `Approval #${this.record?.id ?? ''}`
            )
        },
        thumbLetter() {
            const s = this.displayName || 'A'
            return (s.replace(/[^A-Za-z]/g, '')[0] || 'A').toUpperCase()
        },
        // Bug Intake #731: an approval the distributor gave on its own
        // responsibility must never read as the client's approval.
        isDistributorOverride() {
            return (
                this.record?.status === 'approved' &&
                this.record?.approved_by_distributor_override === true
            )
        },
        approverLine() {
            const cid = this.record?.client_id
            if (cid == null) return 'No client linked'
            const client = this.store.clientsById[cid]
            const name = client?.name || `client ${cid}`
            if (this.isDistributorOverride) {
                return `Distributor override (on behalf of ${name})`
            }
            return `Approver: ${name}`
        },
    },
    watch: {
        record: {
            immediate: true,
            async handler(rec) {
                if (rec?.artwork_id != null) {
                    await this.store.fetchUsage(rec.artwork_id)
                }
                // Load this record's rework prefill. Read-only and reaches
                // no vendor, so it is safe on selection. Without it the
                // panel would carry the PREVIOUS record's client feedback,
                // which is how one client's words get submitted against
                // another's job.
                //
                // Guarded on the method existing: this panel is mounted with
                // a caller-supplied ``store`` object, and a caller that does
                // not know about rework must not crash the whole detail view
                // over one optional section. ``canSubmitRework`` then reads
                // false and the panel simply does not render.
                if (rec?.id != null && this.store.fetchRework) {
                    await this.store.fetchRework(rec.id)
                }
            },
        },
    },
    methods: {
        async onSendToDigitizer() {
            if (!this.record) return
            try {
                await this.store.sendToDigitizer(this.record.artwork_id)
            } catch (_err) {
                /* store.actionError surfaces in the template */
            }
        },
        async onRemind() {
            if (!this.record) return
            try {
                await this.store.sendReminder(this.record.id)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async onEscalate() {
            if (!this.record) return
            try {
                await this.store.escalate(this.record.id)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async onResend() {
            if (!this.record) return
            try {
                await this.store.resendApprovalRequest(this.record.id)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async onResendToDigitizer() {
            // Resend with corrections after a Changes Requested loop:
            // route the artwork back to the digitizer. The new
            // ArtworkVersion is minted by the digitizer when they
            // upload; here we kick the pipeline_state back to
            // 'digitizing'.
            if (!this.record) return
            try {
                await this.store.sendToDigitizer(this.record.artwork_id)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async onPostMessage(body) {
            if (!this.record) return
            try {
                await this.store.postMessage(this.record.id, body)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async onSubmitRework(instruction) {
            if (!this.record) return
            try {
                await this.store.submitRework(this.record.id, instruction)
            } catch (_err) {
                /* surfaces via store.actionError, rendered in ReworkPanel */
            }
        },
        async onRestoreVersion(versionPayload) {
            if (!this.record?.artwork_id) return
            try {
                await this.store.restoreVersion(
                    this.record.artwork_id,
                    versionPayload,
                )
                // Reload detail so the new current version appears at
                // the top of the version history.
                await this.store.selectApproval(this.record.id)
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
        async openReasonPicker() {
            this.reasonPickerOpen = true
            this.reasonId = null
            this.reasonNotes = ''
            // Lazy-load reasons the first time the picker opens.
            await this.store.fetchBypassReasons()
        },
        cancelReasonPicker() {
            this.reasonPickerOpen = false
            this.reasonId = null
            this.reasonNotes = ''
        },
        onReasonNotes(value) {
            this.reasonNotes = value
        },
        async confirmBypass() {
            if (!this.record || this.reasonId == null) return
            try {
                await this.store.bypass(
                    this.record.id,
                    this.reasonId,
                    this.reasonNotes,
                )
                this.reasonPickerOpen = false
                this.reasonId = null
                this.reasonNotes = ''
            } catch (_err) {
                /* surfaces via store.actionError */
            }
        },
    },
}
</script>

<style scoped>
.aw-detail {
    display: flex;
    flex-direction: column;
    background: var(--tf-bg, #ffffff);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    padding: 18px;
    overflow-y: auto;
    min-height: 0;
}
.aw-detail__placeholder,
.aw-detail__loading {
    text-align: center;
    color: var(--tf-fg-4, #5a6a82);
    padding: 60px 20px;
}
.aw-detail__placeholder-title {
    margin: 0 0 8px;
    font-size: 16px;
    color: var(--tf-fg-2, #2d3748);
}
.aw-detail__placeholder-body {
    margin: 0;
    font-size: 13px;
}
.aw-detail__content {
    display: flex;
    flex-direction: column;
}
.aw-detail__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
}
.aw-detail__thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    border-radius: 8px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    font-size: 22px;
    font-weight: 600;
}
.aw-detail__head-body {
    flex: 1;
    min-width: 0;
}
.aw-detail__title {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-detail__head-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.aw-detail__chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 10px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    font-size: 11px;
}
.aw-detail__chip--status {
    background: rgba(25, 118, 210, 0.14);
    color: #0d47a1;
    font-weight: 500;
}
.aw-detail__action-notice {
    color: #37474f;
    font-size: 12px;
    margin: 0 0 12px;
}
.aw-detail__action-error {
    color: #c62828;
    font-size: 12px;
    margin: 0 0 12px;
}
/* PR-8: Trueview embroidery preview */
.aw-detail__trueview {
    margin: 0 0 16px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--tf-surface-alt, #edf2f7);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
}
.aw-detail__trueview-img {
    display: block;
    max-width: 100%;
    max-height: 320px;
    object-fit: contain;
}
.aw-detail__trueview-pending {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
    padding: 20px;
}
.aw-detail__trueview-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid var(--tf-border, #e2e8f0);
    border-top-color: var(--tf-fg-3, #4a5568);
    border-radius: 50%;
    animation: aw-spin 0.7s linear infinite;
}
@keyframes aw-spin {
    to { transform: rotate(360deg); }
}
.aw-detail__trueview-failed {
    color: var(--tf-fg-4, #5a6a82);
    font-size: 12px;
    padding: 20px;
    text-align: center;
}
.aw-detail__modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.aw-detail__modal {
    background: #ffffff;
    border-radius: 8px;
    width: min(480px, 92vw);
    padding: 20px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}
.aw-detail__modal-title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 600;
}
.aw-detail__modal-intro {
    margin: 0 0 12px;
    font-size: 13px;
    color: var(--tf-fg-3, #4a5568);
}
</style>
